import { useEffect, useState } from 'react';
import { RingLoader } from 'react-spinners';
import dayjs from 'dayjs';
import 'react-datepicker/dist/react-datepicker.css';

import { NewsEvent, NewsEventUtil } from '@/modules/calendar/model/calendar/news-event';
import { Observation } from '@/modules/calendar/model/calendar/observation';
import { InstantAnswers } from '@/modules/calendar/model/calendar/instant-answers';
import theme from '@/shared/theme/index.module.scss';
import { useSubscription, MessageServiceConstants } from '@/core/messaging/export';
import { useTimer } from '@/shared/hooks/export';
import { ApiError } from '@/shared/errors/export';
import { AuctionObservation } from '@/modules/calendar/model/calendar/auction-observation';
import { Permissions, RequirePermissions } from '@/core/permissions/export';
import { UpgradePanel } from '@/shared/session/export';
import CalendarDailyView from '@/modules/calendar/components/data-calendar/list/calendar-daily-view';
import CalendarWeekView from '@/modules/calendar/components/data-calendar/list/calendar-week-view';
import { CalendarEventFilter } from '@/modules/calendar/components/data-calendar/calendar-filters';
import CalendarFilters from '@/modules/calendar/components/data-calendar/calendar-filters';
import CalendarMenu from '@/modules/calendar/components/data-calendar/calendar-menu';
import { selectApi } from '@/modules/calendar/api';
import { useSessionIdentity } from '@/core/auth/export';
import styles from './calendar.module.scss';

interface DataCalendarProps {
  date: string;
  view: 'daily' | 'weekly';
  onNavigate: ({ date, view }: { date: string; view: 'daily' | 'weekly' }) => void;
  onEventSelect: ({ date, event }: { date: string; event: NewsEvent }) => void;
}

export default function Calendar({ date, view, onNavigate, onEventSelect }: DataCalendarProps) {
  const identity = useSessionIdentity();
  const dateFormat = 'YYYY-MM-DD';

  if (date === 'today') {
    date = dayjs().tz().format(dateFormat);
  }

  const [calendar, setCalendar] = useState<NewsEvent[]>();
  const [notifications, setNotifications] = useState<string[]>([]);
  const [notificationLoaded, setNotificationLoaded] = useState<boolean>(false);
  const [notificationError, setNotificationError] = useState<any>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [widgetLoaded, setWidgetLoaded] = useState<boolean>(false);
  const [error, setError] = useState<any>(false);
  const [eventFilter, setEventFilter] = useState<CalendarEventFilter>();
  const [filteredCalendar, setFilteredCalendar] = useState<NewsEvent[]>();
  const [showFilters, setShowFilters] = useState<boolean>(false);

  const unit = (view: string): 'week' | 'day' => (view === 'weekly' ? 'week' : 'day');

  const update = () => {
    const start = dayjs(date).tz().startOf(unit(view)).valueOf();
    const end = dayjs(date).tz().endOf(unit(view)).valueOf();

    selectApi.getCalendar(start, end).then(
      value => {
        value.sort((a, b) => {
          const timeDifference = new Date(a.date).getTime() - new Date(b.date).getTime();

          if (timeDifference !== 0) {
            return timeDifference;
          }

          const countryDiff = a.country.localeCompare(b.country);

          if (countryDiff !== 0) return countryDiff;

          return a.title.localeCompare(b.title);
        });

        setCalendar(value);
      },
      (error: ApiError) => {
        if (!loaded) {
          setLoaded(true);
          setError(error);
          setCalendar([]);
        }
      },
    );
  };

  const reload = () => {
    setLoaded(false);
    update();
  };

  const reloadNotifications = () => {
    setNotificationLoaded(false);
    updateNotifications();
  };

  const updateNotifications = () => {
    selectApi
      .getCalendarNotifications()
      .then(value => {
        setNotificationLoaded(true);
        setNotifications(value);
        setNotificationError(false);
      })
      .catch(error => {
        setNotificationLoaded(true);
        setNotificationError(error);
      });
  };

  useTimer(
    30000,
    () => {
      update();
      updateNotifications();
    },
    true,
  );

  useEffect(() => {
    if (eventFilter && calendar) {
      setFilteredCalendar(calendar.filter(eventFilter.filter));
      setLoaded(true);
    }
  }, [eventFilter, calendar]);

  useSubscription<Observation[]>(MessageServiceConstants.SELECT_TOPIC, message => {
    if (!calendar) return;

    message.forEach(observation => {
      calendar.forEach(value => {
        NewsEventUtil.mergeObservations(value, observation);
      });
    });

    setCalendar([...calendar]);
  });

  useSubscription<InstantAnswers[]>(MessageServiceConstants.INSTANT_ANSWERS_TOPIC, message => {
    if (!calendar) return;

    message.forEach(observation => {
      calendar.forEach(value => {
        NewsEventUtil.mergeInstantAnswers(value, observation);
      });
    });

    setCalendar([...calendar]);
  });

  useSubscription<AuctionObservation[]>(MessageServiceConstants.AUCTION_RESULTS_TOPIC, message => {
    if (!calendar) return;

    message.forEach(observation => {
      calendar.forEach(value => {
        if (NewsEventUtil.mergeAuctionData(value, observation)) console.log('Merged auction data');
      });
    });

    setCalendar([...calendar]);
  });

  useEffect(() => {
    if (notificationLoaded && loaded) {
      setWidgetLoaded(true);
    } else {
      setWidgetLoaded(false);
    }
  }, [notificationLoaded, loaded, setWidgetLoaded]);

  const calendarView = (
    <>
      {view === 'daily' && (
        <CalendarDailyView
          calendar={filteredCalendar}
          setEvent={(e: NewsEvent) => onEventSelect({ date, event: e })}
        />
      )}
      {view === 'weekly' && (
        <CalendarWeekView
          calendar={filteredCalendar}
          setEvent={(e: NewsEvent) => onEventSelect({ date, event: e })}
          start={dayjs(date).tz()}
        />
      )}
    </>
  );

  return (
    <div className="container-element h-100">
      <div className={'h-100 d-flex flex-column container-responsive-text'}>
        <div className={`pb-2 sticky-top z-1 border-bottom ${styles.stickyHeader}`}>
          <CalendarMenu
            view={view}
            date={date}
            onNavigate={onNavigate}
            onToggleFilters={() => {
              setShowFilters(!showFilters);
            }}
            key={'menu'}
            filtersActive={eventFilter?.filtersActive}
            notifications={notifications}
            notificationError={notificationError}
            notificationLoaded={widgetLoaded}
            reloadNotifications={reloadNotifications}
          />

          <CalendarFilters
            onEventFilterChange={f => {
              setEventFilter(f);
            }}
            showFilters={showFilters}
          />
        </div>

        <div className={'overflow-auto h-100'}>
          {widgetLoaded && error && (
            <div className={'alert alert-danger'}>
              Error connecting to remote service.
              <button
                className={'btn btn-link'}
                onClick={() => reload()}
                style={{ fontFamily: 'Inter' }}
              >
                Retry
              </button>
            </div>
          )}

          {!error && widgetLoaded && (
            <div className={'h-100 overflow-auto'}>
              <RequirePermissions
                allow={Permissions.ANY_SELECT_PRODUCT}
                fallback={
                  <UpgradePanel
                    contentName={'HSC'}
                    missingPermission={'read:select:any'}
                    identity={identity}
                  />
                }
              >
                <RequirePermissions
                  deny={Permissions.EXCEL_PACKAGE}
                  fallback={
                    <RequirePermissions
                      allow={Permissions.HSC_PACKAGE}
                      fallback={
                        <UpgradePanel
                          contentName={'HSC'}
                          missingPermission={Permissions.HSC_PACKAGE}
                          identity={identity}
                        />
                      }
                    >
                      {calendarView}
                    </RequirePermissions>
                  }
                >
                  {calendarView}
                </RequirePermissions>
              </RequirePermissions>
            </div>
          )}
          {!widgetLoaded && (
            <div className={'p-5 m-5 d-flex justify-content-around flex-row'}>
              <RingLoader color={theme.info} size={100} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
